import { useEffect, useState } from 'react';

// Cross-app "add to home screen" affordance (2026-07-22), shared via
// @lemontaigu/reception's source-export (./*) — reception, admin (bos
// superset), and staff all render it in their nav.
//
// Android/Chrome fires `beforeinstallprompt` automatically once the
// manifest + service-worker installability criteria are met; left alone it
// only shows the browser's own easy-to-miss mini-infobar. We capture the
// event and surface an explicit in-app "Installer" button instead, so the
// operator/staff member gets an unmissable, one-tap path onto the home
// screen. iOS Safari has NO programmatic install API at all — Apple only
// exposes it through Share → « Sur l'écran d'accueil » — so there we show a
// one-line instruction instead. Both banners persist their dismissal in
// localStorage (per origin, i.e. per app) so they don't nag every session.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwaInstallHintDismissed';

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    (window.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function InstallHint() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (isStandalone()) return;
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (isStandalone() || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (deferred) {
    return (
      <div className="pwa-hint">
        <span>📲 Installez l’application sur cet appareil pour un accès plus rapide.</span>
        <button
          type="button"
          onClick={() => {
            void deferred.prompt().then(() => setDeferred(null));
          }}
        >
          Installer
        </button>
        <button type="button" className="pwa-hint-close" onClick={dismiss} aria-label="Fermer">
          ×
        </button>
      </div>
    );
  }

  if (isIos()) {
    return (
      <div className="pwa-hint">
        <span>
          📲 Ajoutez cette application à l’écran d’accueil : appuyez sur Partager (⬆️) puis « Sur l’écran
          d’accueil ».
        </span>
        <button type="button" className="pwa-hint-close" onClick={dismiss} aria-label="Fermer">
          ×
        </button>
      </div>
    );
  }

  return null;
}
